import React, { useState, useEffect, useMemo } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Sun, Moon, Palmtree } from 'lucide-react';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import useEmployeeProfile from '../../hooks/useEmployeeProfile';
import { leaveService } from '../../api/services';

const ManagerCalendar = () => {
  const { setHeader } = usePageHeader();
  const userProfile = useEmployeeProfile();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [workCalendarData, setWorkCalendarData] = useState([]);
  const [leaveEntries, setLeaveEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setHeader({
      icon: CalendarDays,
      title: 'My Absence Calendar',
      subtitle: 'Calendar-style view of your work schedule, holidays, and leave requests.',
    });
    return () => setHeader({ icon: null, title: '', subtitle: '' });
  }, [setHeader]);

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getMonthRange = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return {
      start: formatDateLocal(new Date(year, month, 1)),
      end: formatDateLocal(new Date(year, month + 1, 0)),
    };
  };

  useEffect(() => {
    const fetchCalendarData = async () => {
      if (!userProfile?.employeeNo) return;

      const { start, end } = getMonthRange(currentDate);
      setIsLoading(true);
      setError(null);

      try {
        const [calendarResponse, leaveResponse] = await Promise.all([
          leaveService.getWorkCalendarDays(userProfile.employeeNo, {
            $filter: `CalendarDate ge ${start}T00:00:00Z and CalendarDate le ${end}T23:59:59Z`,
            $orderby: 'CalendarDate asc',
          }),
          leaveService.getLeaveRequestsByEmployee(userProfile.employeeNo, {
            $filter: `LeaveDate ge ${start}T00:00:00Z and LeaveDate le ${end}T23:59:59Z`,
            $orderby: 'LeaveDate asc',
          }),
        ]);

        setWorkCalendarData(
          (calendarResponse.data?.value || []).map((day) => ({
            date: day.CalendarDate.split('T')[0],
            status: day.Status,
            workingHours: day.WorkingHours || 0,
            calendarId: day.CalendarId,
          }))
        );

        setLeaveEntries(
          (leaveResponse.data?.value || []).map((leave) => ({
            date: leave.LeaveDate.split('T')[0],
            hours: leave.Amount || 0,
            type: leave.LeaveType || 'Leave',
            status: leave.Status || 'Unknown',
            requestId: leave.RequestId,
            comment: leave.Comment || '',
          }))
        );
      } catch (err) {
        console.error('Failed to fetch calendar data:', err);
        setError('Failed to load calendar data');
      } finally {
        setIsLoading(false);
      }
    };

    if (!userProfile.isLoading && userProfile.employeeNo) {
      fetchCalendarData();
    }
  }, [userProfile.isLoading, userProfile.employeeNo, currentDate]);

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const todayStr = formatDateLocal(new Date());
    const days = [];

    for (let i = 0; i < firstDayOfMonth.getDay(); i++) {
      days.push(null);
    }
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push({ day, date: dateStr, isToday: todayStr === dateStr });
    }
    return days;
  }, [currentDate]);

  const workCalendarMap = useMemo(() => {
    const map = {};
    workCalendarData.forEach((item) => { map[item.date] = item; });
    return map;
  }, [workCalendarData]);

  const leaveMap = useMemo(() => {
    const map = {};
    leaveEntries.forEach((item) => {
      if (!map[item.date]) map[item.date] = [];
      map[item.date].push(item);
    });
    return map;
  }, [leaveEntries]);

  const getDayStyle = (dateStr) => {
    const workDay = workCalendarMap[dateStr];
    const leaves = leaveMap[dateStr];

    if (leaves && leaves.length > 0) {
      const s = leaves[0].status;
      if (s === 'Approved' || s === 'Completed')
        return { bg: 'bg-green-100 border-green-300', text: 'text-green-800', label: leaves[0].type, icon: null };
      if (s === 'Pending' || s === 'Submitted' || s === 'Draft' || s === 'InReview')
        return { bg: 'bg-yellow-100 border-yellow-300', text: 'text-yellow-800', label: leaves[0].type, icon: null };
      if (s === 'Rejected')
        return { bg: 'bg-red-100 border-red-300', text: 'text-red-800', label: leaves[0].type, icon: null };
      if (s === 'Cancelled' || s === 'Canceled')
        return { bg: 'bg-gray-100 border-gray-300', text: 'text-gray-800', label: leaves[0].type, icon: null };
      return { bg: 'bg-gray-100 border-gray-300', text: 'text-gray-700', label: leaves[0].type, icon: null };
    }

    if (workDay) {
      if (workDay.status === 'Holiday')
        return { bg: 'bg-orange-100 border-orange-300', text: 'text-orange-800', label: 'Holiday', icon: Palmtree };
      if (workDay.status === 'Closed')
        return { bg: 'bg-gray-100 border-gray-300', text: 'text-gray-500', label: 'Weekend', icon: Moon };
      if (workDay.status === 'Open')
        return { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', label: `${workDay.workingHours}h`, icon: Sun };
    }

    return { bg: 'bg-white border-gray-200', text: 'text-gray-400', label: '', icon: null };
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800 min-w-[180px] text-center">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button
              onClick={() => setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Next month"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <button
            onClick={() => setCurrentDate(new Date())}
            className="px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            Today
          </button>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-50 border border-blue-200"></div>
            <span className="text-xs text-gray-600">Working Day</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-gray-100 border border-gray-300"></div>
            <span className="text-xs text-gray-600">Weekend/Closed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-orange-100 border border-orange-300"></div>
            <span className="text-xs text-gray-600">Holiday</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
            <span className="text-xs text-gray-600">Approved Leave</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-300"></div>
            <span className="text-xs text-gray-600">Pending Leave</span>
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
            <p className="ml-3 text-gray-600">Loading calendar data...</p>
          </div>
        )}

        {/* Error */}
        {error && !isLoading && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Calendar Grid */}
        {!isLoading && !error && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
              {dayNames.map((day) => (
                <div
                  key={day}
                  className="p-2 text-center text-sm font-medium text-gray-600 border-r last:border-r-0 border-gray-200"
                >
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {calendarDays.map((dayInfo, index) => {
                if (!dayInfo) {
                  return (
                    <div
                      key={`empty-${index}`}
                      className="min-h-[80px] sm:min-h-[100px] p-2 bg-gray-50 border-r border-b last:border-r-0 border-gray-200"
                    />
                  );
                }
                const style = getDayStyle(dayInfo.date);
                const Icon = style.icon;
                return (
                  <div
                    key={dayInfo.date}
                    className={`min-h-[80px] sm:min-h-[100px] p-2 border-r border-b last:border-r-0 border-gray-200 ${style.bg} transition-colors hover:opacity-90`}
                  >
                    <div className="flex flex-col h-full">
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className={`text-sm font-medium ${
                            dayInfo.isToday
                              ? 'bg-indigo-600 text-white w-7 h-7 rounded-full flex items-center justify-center'
                              : style.text
                          }`}
                        >
                          {dayInfo.day}
                        </span>
                        {Icon && <Icon className={`w-4 h-4 ${style.text}`} />}
                      </div>
                      {style.label && (
                        <div className="flex-1">
                          <span className={`text-[10px] sm:text-xs font-medium ${style.text} block truncate`}>
                            {style.label}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Summary Stats */}
        {!isLoading && !error && workCalendarData.length > 0 && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">
                {workCalendarData.filter((d) => d.status === 'Open').length}
              </p>
              <p className="text-sm text-blue-600">Working Days</p>
            </div>
            <div className="bg-gray-100 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-700">
                {workCalendarData.filter((d) => d.status === 'Closed').length}
              </p>
              <p className="text-sm text-gray-600">Weekends/Closed</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-orange-700">
                {workCalendarData.filter((d) => d.status === 'Holiday').length}
              </p>
              <p className="text-sm text-orange-600">Holidays</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-700">
                {leaveEntries.filter((l) => l.status === 'Approved' || l.status === 'Completed').length}
              </p>
              <p className="text-sm text-green-600">Approved Leave Days</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerCalendar;
